import type { ReactNode } from "react";
import { cx } from "../cx";
import { Icon } from "../icons/Icon";

/**
 * Conexión (fuente con estado): viva (on), propuesta (wait), fantasma (ghost).
 * `listed` = la variante de lista de la cuenta (fila con separador y acto a la
 * derecha); con ella, «Formas de entrar» y Conexiones comparten forma porque
 * comparten identidad (ADR-11, decisión de composición de cuenta.html).
 */
export function Conn({ lg, name, status, on, wait, ghost, pulse, listed, act, stale }: { lg: ReactNode; name: ReactNode; status: ReactNode; on?: boolean | undefined; wait?: boolean | undefined; ghost?: boolean | undefined; pulse?: boolean | undefined; listed?: boolean | undefined; act?: ReactNode | undefined; stale?: boolean | undefined }) {
  return (
    <div className={cx("sk-conn", on && "sk-on", wait && "sk-wait", ghost && "sk-ghost", listed && "sk-conn--listed", stale && "sk-stale")}>
      <span className="sk-conn-lg">{lg}</span>
      <div>
        <div className="sk-conn-nm">{name}</div>
        <div className="sk-conn-st"><span className={cx("sk-dot", pulse && "sk-dot--pulse")} />{status}</div>
      </div>
      {act}
    </div>
  );
}
/** El acto de una fila de conexión (empujado a la derecha por el sistema). */
export function ConnAct({ children }: { children: ReactNode }) {
  return <span className="sk-conn-act">{children}</span>;
}
/** Lista de definición del sistema (la ficha del inspector). `num`: el valor es una cifra (`dd.sk-num`, mono y tabular). */
export function Dl({ items, style, labelledBy }: { items: ReadonlyArray<{ dt: ReactNode; dd: ReactNode; num?: boolean | undefined }>; style?: React.CSSProperties | undefined; labelledBy?: string | undefined }) {
  return <dl className="sk-dl" style={style} aria-labelledby={labelledBy}>{items.map((it, i) => <div key={i} style={{ display: "contents" }}><dt>{it.dt}</dt><dd className={it.num === true ? "sk-num" : undefined}>{it.dd}</dd></div>)}</dl>;
}
/** Etiquetas de máquina: IA / determinista / puerta del sí. */
export function MachineTag({ kind, children }: { kind: "ia" | "det" | "gate"; children: ReactNode }) {
  return <span className={`sk-machinetag sk-machinetag--${kind}`}>{children}</span>;
}
/**
 * Turno de conversación (me = el directivo). En el inspector del Taller es
 * un `div`; en el Despacho, un `article` que entra en escena (`rise` + el
 * retardo escalonado de la canónica).
 */
const TURN_DELAY = { 1: "sk-anim-d1", 2: "sk-anim-d2", 3: "sk-anim-d3" } as const;
export function Turn({ me, name, tm, as = "div", rise, delay, className, children, ...rest }: { me?: boolean | undefined; name: ReactNode; tm: ReactNode; as?: "div" | "article" | undefined; rise?: boolean | undefined; delay?: 1 | 2 | 3 | undefined; className?: string | undefined; children: ReactNode } & Omit<React.HTMLAttributes<HTMLElement>, "className" | "children">) {
  const Tag = as;
  return (
    <Tag className={cx("sk-turn", me && "sk-me", rise && "sk-anim-rise", delay !== undefined && TURN_DELAY[delay], className)} {...rest}>
      <div className="sk-who"><span className="sk-dot" /><span className="sk-name">{name}</span><span className="sk-tm">{tm}</span></div>
      {children}
    </Tag>
  );
}
/** Prosa del asesor dentro de una card (sk-card-prose). */
export function Prose({ children, style }: { children: ReactNode; style?: React.CSSProperties | undefined }) {
  return <p className="sk-card-prose" style={style}>{children}</p>;
}
/** Un documento adjunto: su nombre y lo que se dice de él («6 páginas»). */
export interface Attachment {
  name: string;
  meta?: ReactNode | undefined;
  /** Se está leyendo: spinner en el sitio del glifo y «leyendo…». */
  reading?: boolean | undefined;
}
/**
 * La ficha de fichero: la fila de fichero en forma compacta, para vivir
 * dentro de otra pieza (el composer, la burbuja de un turno). Con
 * `onRemove` lleva «quitar»; en un turno ya enviado, no. El lector la oye
 * como «Documento adjunto: …» (el glifo no dice nada), y el nombre
 * recortado se lee entero al pasar por encima.
 */
export function FileChip({ name, meta, reading, readingLabel = "leyendo…", onRemove, removeLabel = "quitar" }: Attachment & { readingLabel?: string | undefined; onRemove?: (() => void) | undefined; removeLabel?: string | undefined }) {
  const said = reading === true ? readingLabel : meta;
  const hasMeta = said !== undefined && said !== null && said !== false && said !== "";
  return (
    <span className="sk-filechip">
      {reading === true ? <span className="sk-spinner" aria-hidden="true" /> : <Icon name="file" />}
      <span className="sk-vh">Documento adjunto: </span>
      <span className="sk-filechip-name" title={name}>{name}</span>
      {hasMeta && <span className="sk-file-meta">{said}</span>}
      {onRemove !== undefined && reading !== true && <button type="button" className="sk-btn sk-btn--link" aria-label={`Quitar ${name}`} onClick={onRemove}>{removeLabel}</button>}
    </span>
  );
}
/**
 * La fila de fichero (`sk-filerow`): el glifo, el nombre, su dato
 * (`sk-file-meta`: «412 KB · esperando tu respuesta») y, detrás, lo que la
 * pantalla ponga — una barra de progreso, «quitar». `invalid`: el fichero
 * no se admite (`sk-invalid`).
 */
export function FileRow({ name, meta, invalid, children }: { name: ReactNode; meta?: ReactNode | undefined; invalid?: boolean | undefined; children?: ReactNode }) {
  return (
    <div className={cx("sk-filerow", invalid === true && "sk-invalid")}>
      <Icon name="file" />{name}{meta !== undefined && <> <span className="sk-file-meta">{meta}</span></>}{children}
    </div>
  );
}
/**
 * La burbuja de tu turno. Con `file`, el documento que mandaste va en su
 * línea, encima de lo escrito (o solo); lo escrito pasa a su propio
 * elemento, que es lo que separa la ficha (`:last-child` no cuenta nodos de
 * texto).
 */
export function Bubble({ children, file }: { children?: ReactNode; file?: Attachment | undefined }) {
  if (file === undefined) return <div className="sk-bubble">{children}</div>;
  const empty = children === undefined || children === null || children === false || children === "";
  return <div className="sk-bubble"><FileChip name={file.name} meta={file.meta} />{!empty && <span>{children}</span>}</div>;
}
export function RailTitle({ children, sub }: { children: ReactNode; sub?: ReactNode | undefined }) {
  return <div className="sk-railtitle">{children}{sub !== undefined && <span> {sub}</span>}</div>;
}
/** Lista de memoria (criterios/decisiones…): título + sublínea por entrada. */
export function MemList({ items, className }: { items: ReadonlyArray<{ b: ReactNode; sub: ReactNode }>; className?: string | undefined }) {
  return (
    <div className={cx("sk-list", className)}>
      {items.map((it, i) => <div key={i}><b>{it.b}</b><span className="sk-sub">{it.sub}</span></div>)}
    </div>
  );
}
